import { gql } from '@apollo/client';

/**
 * Spec 0011 (survey constructor) — ERP-scope queries. Operation names are
 * deliberately different from the backend field names (`surveys`, `survey`,
 * `groupSurveyAssignments`, …) to avoid the codegen result-type collapse.
 *
 * `questionType` is a plain String on the wire
 * (yes_no|text|rating|single_choice|multi_choice|nps); answer `value` is a
 * JSONString parsed only in the `core/hooks/Surveys` transform layer.
 */

export const SurveysListQuery = gql`
  query ErpSurveysList(
    $courseId: ID
    $includeArchived: Boolean
    $search: String
  ) {
    surveys(
      courseId: $courseId
      includeArchived: $includeArchived
      search: $search
    ) {
      id
      title
      courseId
      courseName
      defaultLessonNumber
      isArchived
      hasResponses
      questionsCount
      assignmentsCount
      groupNames
      updatedAt
    }
  }
`;

export const SurveyDetailQuery = gql`
  query ErpSurveyDetail($id: ID!) {
    survey(id: $id) {
      id
      title
      description
      courseId
      courseName
      defaultLessonNumber
      isArchived
      hasResponses
      questions {
        id
        number
        text
        questionType
        metric
        required
        options
      }
    }
  }
`;

export const GroupSurveyAssignmentsQuery = gql`
  query ErpGroupSurveyAssignments($groupId: ID!) {
    groupSurveyAssignments(groupId: $groupId) {
      id
      surveyId
      surveyTitle
      lessonId
      lessonName
      lessonNumber
      lessonDate
      isActive
      origin
      planId
      respondedCount
      eligibleCount
    }
  }
`;

/**
 * Spec 0027 — targeting. `scope` is 'groups' | 'course' and `triggerKind` is
 * 'lesson_number' | 'last_lesson': plain lowercase Strings on the wire, never
 * graphene enums (see SURVEY_PLAN_SCOPES / SURVEY_TRIGGER_KINDS in
 * core/hooks/Surveys/surveyModel.ts).
 */
export const SurveyTargetingQuery = gql`
  query ErpSurveyTargeting($surveyId: ID!) {
    surveyTargeting(surveyId: $surveyId) {
      plans {
        id
        scope
        courseId
        courseName
        onlyActiveGroups
        triggerKind
        triggerLessonNumber
        skipPastLessons
        isActive
        assignmentsCount
        respondedCount
        hasResponses
        groups {
          groupId
          groupName
        }
      }
    }
  }
`;

/**
 * The picker's preview: every candidate group with the lesson the trigger
 * resolves to, or the reason it would be skipped — so «в группе нет занятия
 * №8» is visible before saving instead of arriving in the skip report after.
 */
export const SurveyTargetGroupsQuery = gql`
  query ErpSurveyTargetGroups(
    $surveyId: ID
    $courseId: ID
    $search: String
    $onlyActive: Boolean
    $triggerKind: String
    $triggerLessonNumber: Int
  ) {
    surveyTargetGroups(
      surveyId: $surveyId
      courseId: $courseId
      search: $search
      onlyActive: $onlyActive
      triggerKind: $triggerKind
      triggerLessonNumber: $triggerLessonNumber
    ) {
      groupId
      groupName
      courseId
      courseName
      startDate
      lessonsCount
      isActive
      resolvedLessonId
      resolvedLessonName
      resolvedLessonNumber
      resolvedLessonDate
      resolvedLessonIsOver
      skipReason
      alreadyAttached
    }
  }
`;

/** Pooled results across every group the survey is attached to (spec 0027). */
export const SurveySummaryQuery = gql`
  query ErpSurveySummary(
    $surveyId: ID!
    $groupIds: [ID!]
    $planId: ID
    $dateFrom: Date
    $dateTo: Date
  ) {
    surveySummary(
      surveyId: $surveyId
      groupIds: $groupIds
      planId: $planId
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      survey {
        id
        title
        courseName
      }
      assignmentsCount
      groupsCount
      respondedCount
      eligibleCount
      responseRate
      questionResults {
        question {
          id
          number
          text
          questionType
          metric
          required
          options
        }
        answersCount
        average
        distribution {
          key
          label
          count
        }
      }
      groupBreakdown {
        assignmentId
        groupId
        groupName
        lessonName
        lessonNumber
        lessonDate
        isActive
        origin
        respondedCount
        eligibleCount
        responseRate
        averageRating
      }
    }
  }
`;

/** One teacher's survey feedback (spec 0027) — the card's «Отзывы» tab. */
export const TeacherSurveyFeedbackQuery = gql`
  query ErpTeacherSurveyFeedback(
    $teacherId: ID!
    $courseId: ID
    $groupId: ID
    $dateFrom: Date
    $dateTo: Date
    $bucket: String
  ) {
    teacherSurveyFeedback(
      teacherId: $teacherId
      courseId: $courseId
      groupId: $groupId
      dateFrom: $dateFrom
      dateTo: $dateTo
      bucket: $bucket
    ) {
      teacherId
      teacherName
      average
      ratingsCount
      commentsCount
      trend {
        period
        average
        count
      }
      byCourse {
        id
        name
        average
        count
      }
      byGroup {
        id
        name
        average
        count
      }
      comments {
        studentName
        comment
        createdAt
        courseName
        groupName
      }
    }
  }
`;

/** Cross-teacher table, weakest first (spec 0027). */
export const TeacherSurveyLeaderboardQuery = gql`
  query ErpTeacherSurveyLeaderboard(
    $courseId: ID
    $dateFrom: Date
    $dateTo: Date
    $minResponses: Int
  ) {
    teacherSurveyLeaderboard(
      courseId: $courseId
      dateFrom: $dateFrom
      dateTo: $dateTo
      minResponses: $minResponses
    ) {
      teacherId
      teacherName
      average
      ratingsCount
      commentsCount
      coursesCount
      enoughData
    }
  }
`;

export const SurveyAssignmentResultsQuery = gql`
  query ErpSurveyAssignmentResults($assignmentId: ID!) {
    surveyAssignmentResults(assignmentId: $assignmentId) {
      assignment {
        id
        surveyTitle
        groupId
        groupName
        lessonName
        lessonNumber
        respondedCount
        eligibleCount
      }
      respondents {
        studentId
        fullName
        email
        responded
        submittedAt
        responseId
      }
      questionResults {
        question {
          id
          number
          text
          questionType
          metric
          required
          options
        }
        answersCount
        average
        distribution {
          key
          label
          count
        }
      }
    }
  }
`;

export const SurveyResponseDetailQuery = gql`
  query ErpSurveyResponseDetail($responseId: ID!) {
    surveyResponseDetail(responseId: $responseId) {
      responseId
      studentId
      fullName
      submittedAt
      answers {
        questionId
        value
      }
    }
  }
`;

/**
 * Spec 0038 — analytics. The whole screen is «filter × dimension», so every
 * query below takes the SAME `SurveyAnalyticsFilter` input; the filter panel
 * holds one object and hands it to all of them.
 *
 * Two things to remember when reading the results:
 *  - the date window filters the TRIGGER LESSON's date, not submission time
 *    (otherwise responseRate's numerator and denominator disagree);
 *  - for the `metric` / `questionType` dimensions the roster numbers come
 *    back null on purpose — a topic has no roster — and `label` is the raw
 *    slug, localized here rather than by the server.
 */
export const SurveyAnalyticsQuery = gql`
  query ErpSurveyAnalytics(
    $dimension: String!
    $filter: SurveyAnalyticsFilter
  ) {
    surveyAnalytics(dimension: $dimension, filter: $filter) {
      key
      label
      sortKey
      assignmentsCount
      surveysCount
      responsesCount
      studentsCount
      answersCount
      eligibleCount
      responseRate
      avgScore
      negativeShare
      avgRating
      avgNps
      avgTeacherRating
      yesShare
      lowAnswersShare
    }
  }
`;

export const SurveySignalsQuery = gql`
  query ErpSurveySignals(
    $dimensions: [String!]
    $minSample: Int
    $limit: Int
    $filter: SurveyAnalyticsFilter
  ) {
    surveySignals(
      dimensions: $dimensions
      minSample: $minSample
      limit: $limit
      filter: $filter
    ) {
      kind
      dimension
      key
      label
      value
      previousValue
      delta
      sampleSize
      severity
    }
  }
`;

export const SurveyQuestionBreakdownQuery = gql`
  query ErpSurveyQuestionBreakdown(
    $surveyId: ID!
    $questionId: ID!
    $dimension: String!
    $filter: SurveyAnalyticsFilter
  ) {
    surveyQuestionBreakdown(
      surveyId: $surveyId
      questionId: $questionId
      dimension: $dimension
      filter: $filter
    ) {
      dimension
      question {
        id
        number
        text
        questionType
        metric
        required
        options
      }
      total {
        answersCount
        average
        distribution {
          key
          label
          count
        }
      }
      slices {
        key
        label
        sortKey
        responsesCount
        result {
          answersCount
          average
          distribution {
            key
            label
            count
          }
        }
      }
    }
  }
`;

export const SurveyCommentsQuery = gql`
  query ErpSurveyComments(
    $filter: SurveyAnalyticsFilter
    $search: String
    $sentiment: String
    $first: Int
    $after: String
    $problemOnly: Boolean
  ) {
    surveyComments(
      filter: $filter
      search: $search
      sentiment: $sentiment
      first: $first
      after: $after
      problemOnly: $problemOnly
    ) {
      hasNext
      cursor
      items {
        answerId
        responseId
        questionId
        questionText
        metric
        text
        studentId
        studentName
        groupId
        groupName
        courseName
        surveyId
        surveyTitle
        lessonNumber
        lessonDate
        teacherId
        teacherName
        submittedAt
        score
        sentiment
        insight {
          lang
          textRu
          textPl
          sentiment
          isProblem
          topic
          summary
        }
      }
    }
  }
`;

/** The audience, seen FROM the survey — the reverse of the group-side query. */
export const SurveyAssignmentsQuery = gql`
  query ErpSurveyAssignments(
    $surveyId: ID!
    $groupIds: [ID!]
    $planId: ID
    $onlyActive: Boolean
    $origin: String
    $search: String
  ) {
    surveyAssignments(
      surveyId: $surveyId
      groupIds: $groupIds
      planId: $planId
      onlyActive: $onlyActive
      origin: $origin
      search: $search
    ) {
      assignmentId
      surveyId
      surveyTitle
      groupId
      groupName
      courseId
      courseName
      lessonId
      lessonName
      lessonNumber
      lessonDate
      teacherId
      teacherName
      isActive
      origin
      planId
      createdAt
      respondedCount
      eligibleCount
      responseRate
      averageRating
      avgScore
      negativeShare
    }
  }
`;

/** One student's answers — the student card's «Опросы» tab (spec 0038). */
export const StudentSurveyResponsesQuery = gql`
  query ErpStudentSurveyResponses(
    $studentId: ID!
    $surveyId: ID
    $groupId: ID
    $includePending: Boolean
  ) {
    studentSurveyResponses(
      studentId: $studentId
      surveyId: $surveyId
      groupId: $groupId
      includePending: $includePending
    ) {
      studentId
      fullName
      respondedCount
      pendingCount
      avgScore
      negativeCount
      responses {
        responseId
        assignmentId
        surveyId
        surveyTitle
        groupId
        groupName
        courseName
        lessonId
        lessonName
        lessonNumber
        lessonDate
        teacherId
        teacherName
        submittedAt
        score
        minScore
        scoredAnswersCount
        sentiment
        excerpt
        questions {
          id
          number
          text
          questionType
          metric
          required
          options
        }
        answers {
          questionId
          value
        }
      }
      pending {
        assignmentId
        surveyId
        surveyTitle
        groupId
        groupName
        lessonId
        lessonName
        lessonNumber
        lessonDate
        teacherName
      }
    }
  }
`;

/**
 * Spec 0038 revision — the bulk tagging screen. Without topics the «по теме»
 * dimension answers nothing, and tagging one question at a time inside each
 * survey's constructor is work nobody does.
 */
export const SurveyQuestionTagsQuery = gql`
  query ErpSurveyQuestionTags(
    $onlyUntagged: Boolean
    $courseId: ID
    $search: String
  ) {
    surveyQuestionTags(
      onlyUntagged: $onlyUntagged
      courseId: $courseId
      search: $search
    ) {
      questionId
      surveyId
      surveyTitle
      courseName
      number
      text
      questionType
      metric
      suggestedMetric
    }
  }
`;

/** Drives the «N вопросов без темы» prompt. */
export const SurveyUntaggedCountQuery = gql`
  query ErpSurveyUntaggedCount {
    surveyUntaggedCount
  }
`;

/** Respondents × questions — the survey-level answer table. */
export const SurveyResponseMatrixQuery = gql`
  query ErpSurveyResponseMatrix($surveyId: ID!, $groupIds: [ID!]) {
    surveyResponseMatrix(surveyId: $surveyId, groupIds: $groupIds) {
      survey {
        id
        title
      }
      questions {
        id
        number
        text
        questionType
        metric
        required
        options
      }
      rows {
        responseId
        studentId
        fullName
        groupId
        groupName
        lessonNumber
        submittedAt
        score
        sentiment
        answers {
          questionId
          value
        }
      }
    }
  }
`;

/**
 * Spec 0098 — the group's surveys by lesson + the per-topic cut. Operation
 * name differs from the field (`surveyGroupTimeline`) on purpose.
 */
export const SurveyGroupTimelineQuery = gql`
  query ErpSurveyGroupHistory($groupId: ID!) {
    surveyGroupTimeline(groupId: $groupId) {
      rows {
        assignmentId
        surveyId
        surveyTitle
        lessonNumber
        lessonDate
        eligible
        responded
        avgScore
        negativeShare
        avgRating
        avgNps
      }
      metrics {
        metric
        avgScore
        answers
      }
    }
  }
`;

/**
 * Spec 0098 — the group's students × their survey answers, rolled up on the
 * server in one round-trip (was N per-attachment + per-survey reads). The
 * operation name differs from the field (`surveyGroupStudents`) on purpose.
 */
export const SurveyGroupStudentsQuery = gql`
  query ErpSurveyGroupStudents($groupId: ID!) {
    surveyGroupStudents(groupId: $groupId) {
      studentId
      studentName
      eligible
      responded
      avgScore
      negativeCount
      lastAnsweredAt
      lastComment
      lastCommentAt
      lastCommentInsight {
        lang
        textRu
        textPl
        sentiment
        isProblem
        topic
        summary
      }
    }
  }
`;

/** Spec 0098 — AI digests of a scope (course | group | survey), newest first. */
export const SurveyDigestsQuery = gql`
  query ErpSurveyDigestList($scope: String!, $scopeId: ID!, $limit: Int) {
    surveyDigests(scope: $scope, scopeId: $scopeId, limit: $limit) {
      id
      scope
      scopeId
      status
      textRu
      textPl
      model
      error
      createdAt
      finishedAt
      byName
    }
  }
`;
